import { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';


// Components
import Navbar from './components/navbar';
import ScrollToTop from './components/Scroll-to-top';
import GlobalFooter from './components/footer';
import ProtectedRoute from './components/ProtectedRoutes';

// Public Pages
import Homepage from './pages/home';
import Events from './pages/events';
import About from './pages/about';
import Contact from './pages/contact';
import Error from './pages/error';
import Gallery from './pages/gallery';
import Vendors from './pages/vendors';
import Eventpage from './pages/event-page';
import VendorsDec from './pages/vendors-december';
import Xmaspage from './pages/xmas-2024';
import ExtrasPage from './pages/ExtrasPage';
import PrivacyPolicy from './pages/PrivacyProfile';
import ThankYouVendor from './pages/thankyou';
import VendorMapPage from './pages/VendorMapPage';
import Summer2026 from './pages/Events/Summer2026';
import ChristmasFestival2026 from './pages/Events/Christmas_2026';
import VendorPublicProfile from './pages/Vendor/VendorPublicProfile';
import VendorsDirectory from './pages/VendorsDirectory';

// Admin Pages
import AdminVendors from './pages/Admin/admin-vendors';
import AdminDashboard from './pages/Admin/admin-dash';
import AdminLayout from './pages/Admin/admin-layout';
import Login from './pages/Admin/Login';
import AcceptedVendors from './pages/Admin/AcceptedVendors';
import SlotGridDemo from './pages/Admin/Layout';
import MediaLibrary from './pages/Admin/Medialibrary';
import VendorDocsDownloader from './pages/Admin/VendorDocsDownloader';
import AdminSignupRequests from './pages/Admin/AdminSignupRequests';
import AdminEmailsLayout from './pages/Admin/AdminEmailsLayout';
import ComposeEmailView from './pages/Admin/ComposeEmailView';
import EmailArchive from './pages/Admin/EmailArchive';
import PaymentAuditTable from './pages/Admin/PaymentAuditTable';

// Vendor Pages — auth
import VendorLogin from './pages/Vendor/Login';
import VendorSignup from './pages/Vendor/VendorSignup';
import VendorAuthGuard from './pages/Vendor/VendorAuthGuard';
import VendorResetPassword from './pages/Vendor/VendorResetPassword';

// Vendor Pages — persistent-account nav/pages
import VendorLayout from './pages/Vendor/VendorLayout';
import VendorDashboardHome from './pages/Vendor/VendorDashboardHome';
import VendorAccount from './pages/Vendor/VendorAccount';
import VendorEvents from './pages/Vendor/VendorEvents';
import VendorApply from './pages/Vendor/VendorApply';
import VendorEventWorkspace from './pages/Vendor/VendorEventWorkspace';
import VendorEventOnboardingGuard from './pages/Vendor/VendorEventOnboardingGuard';
import VendorDocuments from './pages/Vendor/VendorDocuments';
import VendorPayments from './pages/Vendor/VendorPayments';
import VendorNotifications from './pages/Vendor/VendorNotifications';
import GlobalVillage2026 from './pages/Events/Global_village_2026';
import VendorUpdatePage from './pages/Vendor/VendorUpdatePage';
import VendorProducts from './pages/Vendor/VendorProducts';
import VendorProfileModal from './pages/Vendor/VendorProfileModal';

const LabEditor = lazy(() => import('./pages/Admin/lab/LabEditor'));
const LAB_ENABLED = process.env.REACT_APP_ENABLE_LAB === 'true';

// Routes that take over the full viewport — added '/v/' for store slugs
const FULLSCREEN_ROUTES = ['/layout', '/admin', '/vendor', '/v/'];

function Layout() {
  const location = useLocation();
  const path = location.pathname.toLowerCase();

  const backgroundLocation = location.state?.backgroundLocation;

  // Check standard static fullscreen routes (admin, layout, vendor dashboard)
  const isBaseFullscreen = ['/layout', '/admin', '/vendor'].some(r => path.startsWith(r));

  // /v/:slug is ONLY fullscreen if accessed directly (no backgroundLocation from Directory)
  const isDirectVendorProfile = path.startsWith('/v/') && !backgroundLocation;

  const isFullscreenRoute = isBaseFullscreen || isDirectVendorProfile;

  // Fullscreen routes (admin, vendor dashboard, or direct /v/:slug visits)
  if (isFullscreenRoute) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="Layout" element={<VendorMapPage />} />

          {/* Direct link fallback for store slug (full screen) */}
          <Route path="v/:slug" element={<VendorPublicProfile />} />

          <Route path="/admin">
            <Route path="login" element={<Login />} />

            {LAB_ENABLED && (
              <Route
                path="lab"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <LabEditor />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            )}

            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="vendors" element={<AdminVendors />} />
              <Route path="signup-requests" element={<AdminSignupRequests />} />
              <Route path="accepted" element={<AcceptedVendors />} />
              <Route path="Layout" element={<SlotGridDemo />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="download" element={<VendorDocsDownloader />} />
              <Route path="emails" element={<AdminEmailsLayout />}>
                <Route index element={<ComposeEmailView />} />
                <Route path="archive" element={<EmailArchive />} />
                
              </Route>
              <Route path="audit" element={<PaymentAuditTable/>}/>
            </Route>
          </Route>

          <Route path="/vendor">
            <Route path="login" element={<VendorLogin />} />
            <Route path="signup" element={<VendorSignup />} />
            <Route path="reset-password" element={<VendorResetPassword />} />

            <Route element={<VendorAuthGuard />}>
              <Route element={<VendorLayout />}>
                <Route index element={<VendorDashboardHome />} />
                <Route path="dashboard" element={<VendorDashboardHome />} />
                <Route path="account" element={<VendorAccount />} />
                <Route path="products" element={<VendorProducts />} />
                <Route path="events" element={<VendorEvents />} />
                <Route path="apply/:eventKey" element={<VendorApply />} />
                <Route element={<VendorEventOnboardingGuard />}>
                  <Route path="events/:eventKey" element={<VendorEventWorkspace />} />
                </Route>
                <Route path="documents" element={<VendorDocuments />} />
                <Route path="payments" element={<VendorPayments />} />
                <Route path="notifications" element={<VendorNotifications />} />
                <Route path="updates/:updateId" element={<VendorUpdatePage />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </>
    );
  }

  // Shell Layout with Modal Overlay Support
  return (
    <>
      <Navbar />
      <ScrollToTop />
      <div className="main-content">
        <Routes location={backgroundLocation || location}>
          <Route path="/" element={<Homepage />} />
          <Route path="home" element={<Homepage />} />
          <Route path="events" element={<Events />} />
          <Route path="contact" element={<Contact />} />
          <Route path="about" element={<About />} />
          <Route path="vendors" element={<Vendors />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="eventpage" element={<Eventpage />} />
          <Route path="events/summer-2026" element={<Summer2026 />} />
          <Route path="events/global-village" element={<GlobalVillage2026 />} />
          <Route path='events/christmas' element={<ChristmasFestival2026/>}/>
          <Route path="Vendors-dec" element={<VendorsDec />} />
          <Route path="xmas-2024" element={<Xmaspage />} />
          <Route path="extras" element={<ExtrasPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="thank-you" element={<ThankYouVendor />} />
          <Route path="v/:slug" element={<VendorPublicProfile />} />
          <Route path="Directory" element={<VendorsDirectory />} />
          <Route path="*" element={<Error />} />
        </Routes>
        <GlobalFooter />
      </div>

      {/* Modal renders over Directory when backgroundLocation exists */}
      {backgroundLocation && (
        <Routes>
          <Route path="v/:slug" element={<VendorProfileModal />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <Layout />
      </Router>
    </HelmetProvider>
  );
}

export default App;